import { PatientTransactionItemType, createDecimal } from '@chiroup/core';
import { MeContext } from '../../../../../../contexts/me.context';
import Decimal from 'decimal.js';
import { useContext, useMemo } from 'react';
import useDisciplines from '../../../../../settings/clinic/useDisciplines';

export const useTotals = ({
  appointmentServices = [],
  treatmentId,
  disciplineId,
  servicesTotal,
}: {
  appointmentServices: Partial<PatientTransactionItemType>[] | undefined;
  treatmentId: number;
  disciplineId: number;
  servicesTotal: number;
}) => {
  const { data: disciplines } = useDisciplines();
  const { selectedLocationFull } = useContext(MeContext);

  const totalTax = useMemo(() => {
    const taxableAmounts = appointmentServices.reduce(
      (acc: Decimal, service) => {
        if (service.salesTax && !service.priceIncludesTax) {
          return acc.plus(createDecimal(service?.amount ?? 0));
        }
        return acc;
      },
      createDecimal(0),
    );

    return taxableAmounts
      .times(createDecimal(selectedLocationFull?.serviceTaxRate ?? 0))
      .toNumber();
  }, [appointmentServices, selectedLocationFull?.serviceTaxRate]);

  const servicesCost = useMemo(() => {
    return appointmentServices.reduce((acc, service) => {
      return acc.add(createDecimal(service?.amount ?? 0));
    }, createDecimal(0));
  }, [appointmentServices]);

  const treatmentCost = useMemo(() => {
    if (disciplines?.length && treatmentId && disciplineId) {
      const discipline = disciplines?.find((d) => d.ID === disciplineId);
      const treatmentPrice = discipline?.treatments.find(
        (t) => t.ID === treatmentId,
      )?.price;
      return createDecimal(
        treatmentPrice ? String(treatmentPrice).replace('$', '') : 0,
      );
    }
    return createDecimal(0);
  }, [treatmentId, disciplineId, disciplines]);

  const getSubTotal = (servicesTotal = 0) => {
    const services = servicesCost.toNumber();
    const treatmentPrice = treatmentCost.toNumber();
    const serviceCostToUse = servicesTotal === 0 ? services : servicesTotal;
    return serviceCostToUse === 0
      ? treatmentPrice?.toFixed(2)
      : serviceCostToUse;
  };

  const subTotal = getSubTotal(servicesTotal);

  const total = createDecimal(getSubTotal(servicesTotal))
    .plus(totalTax)
    .toNumber();

  return {
    totalTax,
    servicesCost,
    treatmentCost,
    total,
    subTotal,
  };
};
